import get from "lodash/get";
import {StationCycleState} from "../Constants";
import {areSameCycles} from "../components/util/sort-paths/CommonUtils";

/**
 * Maximum number of sort path configurations allowed per (station, cycle) once the
 * 1:1 cycle-to-setting filter is relaxed (design Meeting Notes 08/18).
 */
export const MAX_CONFIGS_PER_CYCLE = 10;

/**
 * Normalizes one cycleList entry ({stationCode, cycle: {cycleName, cycleId}}) into the
 * StationCycle shape compared by areSameCycles, defaulting a missing stationCode to the
 * owning form/setting's nodeId.
 */
const toStationCycle = (stationCode, cycleName) => ({
    [StationCycleState.STATION_CODE]: stationCode,
    [StationCycleState.CYCLE]: {[StationCycleState.CYCLE_NAME]: cycleName},
});

/**
 * Expands a form's commonFormData, or an existing sort path setting, into the list of
 * StationCycles it targets. A multi-cycle entity carries its cycles in cycleList, each
 * scoped to its own stationCode; otherwise it targets the scalar {nodeId, cycle}.
 *
 * @param entity commonFormData or an existing SortPathSetting
 * @returns {Array} StationCycle objects (empty when no cycle can be derived)
 */
export function getStationCycles(entity) {
    if (!entity) return [];
    const cycleList = entity.cycleList;
    if (Array.isArray(cycleList) && cycleList.length > 0) {
        return cycleList
            .map(c => toStationCycle(
                get(c, [StationCycleState.STATION_CODE], entity.nodeId),
                get(c, [StationCycleState.CYCLE, StationCycleState.CYCLE_NAME])))
            .filter(c => !!c[StationCycleState.CYCLE][StationCycleState.CYCLE_NAME]);
    }
    return entity.cycle ? [toStationCycle(entity.nodeId, entity.cycle)] : [];
}

const sharesAnyCycle = (setting, targetCycles) =>
    getStationCycles(setting).some(existing => targetCycles.some(target => areSameCycles(existing, target)));

const normalizeName = (name) => (name || "").trim().toLowerCase();

/**
 * True when an existing setting with the same (case-insensitive, trimmed) name already
 * targets any of the station/cycle pairs the form targets.
 *
 * @param commonFormData the manual form's common data (name, nodeId, cycle, cycleList)
 * @param sortPathConfigurationList existing sort path settings
 * @returns {boolean}
 */
export function isDuplicateNameForCycle(commonFormData, sortPathConfigurationList) {
    const enteredName = normalizeName(get(commonFormData, ["name"]));
    const targetCycles = getStationCycles(commonFormData);
    if (!enteredName || targetCycles.length === 0 || !Array.isArray(sortPathConfigurationList)) {
        return false;
    }
    return sortPathConfigurationList.some(setting =>
        !!setting
        && normalizeName(setting.name) === enteredName
        && sharesAnyCycle(setting, targetCycles));
}

/**
 * True when any station/cycle the form targets already has MAX_CONFIGS_PER_CYCLE (or more)
 * existing settings, so a new configuration must not be created for it.
 *
 * @param commonFormData the manual form's common data (nodeId, cycle, cycleList)
 * @param sortPathConfigurationList existing sort path settings
 * @param limit maximum configurations per station/cycle
 * @returns {boolean}
 */
export function hasReachedConfigLimitForCycle(commonFormData, sortPathConfigurationList,
                                              limit = MAX_CONFIGS_PER_CYCLE) {
    const targetCycles = getStationCycles(commonFormData);
    if (targetCycles.length === 0 || !Array.isArray(sortPathConfigurationList)) {
        return false;
    }
    return targetCycles.some(target =>
        sortPathConfigurationList.filter(setting =>
            !!setting && getStationCycles(setting).some(existing => areSameCycles(existing, target))
        ).length >= limit);
}
